'use client';

import { ResponsiveHeader } from '../components/ResponsiveHeader';
import { ResponsiveFooter } from '../components/ResponsiveFooter';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'motion/react';
import { BookOpen } from 'lucide-react';
import { resources } from '../data/resources';
// Removed SEO component (handled by page metadata)

export function ArticleBlogScreen() {
  const router = useRouter();

  const sortedResources = [...resources].sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  const onNavigate = (page: string) => {
    router.push(page === 'home' ? '/' : `/${page}/`);
  };

  return (
    <div className="min-h-screen bg-[#18181b] flex flex-col relative overflow-hidden">
      <ResponsiveHeader />
      {/* Animated Background Gradient Blobs - Optimized with 2 blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(147, 51, 234, 0.3) 0%, rgba(147, 51, 234, 0.1) 50%, transparent 100%)',
            willChange: 'transform',
          }}
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-0 left-1/2 w-[800px] h-[800px] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, rgba(236, 72, 153, 0.1) 50%, transparent 100%)',
            willChange: 'transform',
          }}
          animate={{
            x: [-100, 100, -100],
            y: [0, -50, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-8 sm:pt-16 pt-32 lg:mt-24 pb-12 lg:pb-20">
        <div className="max-w-7xl mx-auto text-center relative z-10 px-[0px] py-[25px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                Resources
              </span>{' '}
              & Learning
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto">
              Curated guides, articles, and insights to help you build better digital products
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="relative px-4 sm:px-6 lg:px-8 pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {sortedResources.map((resource, index) => {
              // Helper function to get color hex code
              const getColorHex = (colorName: string) => {
                const colors: { [key: string]: string } = {
                  purple: '#a855f7',
                  cyan: '#22d3ee',
                  emerald: '#10b981',
                  yellow: '#fbbf24',
                  pink: '#ec4899',
                  violet: '#8b5cf6',
                  blue: '#3b82f6',
                  indigo: '#6366f1',
                  orange: '#f97316',
                };
                return colors[colorName] || colors.orange;
              };

              const colorHex = getColorHex(resource.color);

              return (
                <Link key={index} href={`/resources/${resource.slug}/`} className="block">
                  <motion.article
                    className="group relative bg-zinc-900/50 backdrop-blur-sm border-2 border-white/10 rounded-3xl overflow-hidden cursor-pointer"
                    style={{ willChange: 'transform' }}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1,
                      ease: [0.21, 0.47, 0.32, 0.98],
                    }}
                    whileHover={{
                      scale: 1.03,
                      y: -8,
                      transition: { duration: 0.3 },
                    }}
                  >
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 opacity-0 group-hover:opacity-10 transition-opacity duration-500" />

                    {/* Glowing border effect on hover */}
                    <motion.div
                      className={`absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                      style={{
                        background: `linear-gradient(135deg, transparent, ${colorHex}20, transparent)`,
                        boxShadow: `0 0 40px ${colorHex}50`,
                      }}
                    />

                    <div className="relative p-6 lg:p-8 h-full flex flex-col">
                      {/* Icon and Category */}
                      <div className="flex items-start justify-between mb-4">
                        <motion.div
                          className="w-24 h-24 lg:w-32 lg:h-32 rounded-2xl overflow-hidden shadow-lg relative"
                          style={{ willChange: 'transform' }}
                          whileHover={{ scale: 1.1, rotate: 2 }}
                          transition={{ duration: 0.5 }}
                        >
                          <img
                            src={resource.image}
                            alt={resource.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 opacity-20 group-hover:opacity-40 transition-opacity duration-300" />
                        </motion.div>

                        <span
                          className="flex items-center px-3 py-1 rounded-full text-xs lg:text-sm font-semibold text-white border border-white/20"
                          style={{ backgroundColor: colorHex }}
                        >
                          {resource.category}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 transition-all duration-300">
                        {resource.title}
                      </h3>

                      {/* Description */}
                      <p className="text-stone-300 text-sm lg:text-base leading-relaxed flex-grow">
                        {resource.description}
                      </p>

                      {/* Read More Link - Optimized arrow animation */}
                      <div className="mt-6 flex items-center gap-2 text-white transition-colors group-hover:translate-x-1 transition-transform duration-300">
                        <BookOpen className="w-5 h-5" />
                        <span className="text-sm font-semibold">Read More</span>
                        <span className="inline-block">→</span>
                      </div>
                    </div>

                    {/* Shine effect on hover */}
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
                      initial={false}
                      whileHover={{
                        background: [
                          'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 60%, transparent 100%)',
                          'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 60%, transparent 100%)',
                        ],
                        backgroundPosition: ['-200%', '200%'],
                      }}
                      transition={{ duration: 1.5, ease: 'easeInOut' }}
                    />
                  </motion.article>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-4 sm:px-6 lg:px-8 mt-8 mb-8 pb-20 lg:pb-32">
        <motion.div
          className="max-w-4xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-md border-2 border-purple-500/30 rounded-3xl p-8 lg:p-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-lg lg:text-xl text-white/80 mb-8">
              Get in touch with us and we'll help you find the perfect solution for your project
            </p>
            <motion.button
              onClick={() => onNavigate('contact')}
              className="px-8 lg:px-10 py-4 lg:py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg lg:text-xl font-semibold rounded-full shadow-lg shadow-purple-500/50"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              Contact Us Today
            </motion.button>
          </div>
        </motion.div>
      </section>

      <ResponsiveFooter />
    </div>
  );
}
